<template>
	<Teleport to="body">
		<div ref="el" data-component="photos-modal">
			<span class="bg" />
			<div class="slider-container">
				<PhotosSlideshow :photos="slideshow" class="photos-slideshow" :has-arrows="true" :enter-animation="true" :loaded-callback="onLoaded" />
			</div>

			<CircleBtn ref="closeBtn" class="close-btn" slug="close" size="large" @click="onClose" />
		</div>
	</Teleport>
</template>

<script setup>
import { PhotosSlideshow } from '~/components/fragments'
import { CircleBtn } from '~/components/primitives'

import { useEmitter } from '~/composables/core'

const props = defineProps({
	slideshow: { type: Array, default: () => [] },
	closeFn: { type: Function, default: () => {} },
})

const el = ref(null)
const emitter = useEmitter()
const closeBtn = ref(null)

function handleKey(e) {
	if (e.key === 'Escape') onClose()
}

const init = () => {
	window.addEventListener('keydown', handleKey)
	emitter.emit('modal-open')

	nextTick(() => {
		el.value.classList.add('active')
		closeBtn.value.animateIn()
	})
}

const kill = () => {
	window.removeEventListener('keydown', handleKey)
}

const onLoaded = () => el.value.classList.add('ui-active')

const onClose = () => {
	el.value.classList.add('close')
	closeBtn.value.animateOut()

	setTimeout(() => el.value.classList.remove('active'), 250)
	setTimeout(() => props.closeFn(), 500)
}

onBeforeUnmount(() => kill())
onMounted(() => init())
</script>

<style lang="scss" scoped>
[data-component='photos-modal'] {
	position: fixed;
	top: 0;
	left: 0;

	display: flex;
	width: 100dvw;
	height: 100dvh;
	padding: calc(var(--grid-padding) * 2);

	color: var(--color-white);

	z-index: 10;

	@include max-screen($xsmall) {
		padding: 0;
	}

	&.active {
		cursor: wait;

		.bg {
			background-color: rgb(from var(--color-darkBrown) r g b / 0.98);
		}
	}

	&.ui-active {
		cursor: initial;
	}

	&.close {
		.slider-container {
			transform: scale(0.98) translateY(10px);
			opacity: 0;
		}
	}

	.bg {
		position: absolute;
		inset: 0;

		display: block;
		background-color: rgb(from var(--color-darkBrown) r g b / 0);

		transition: background-color 0.5s ease(out-expo);

		@include max-screen($xsmall) {
			background-color: var(--color-darkBrown);
		}
	}

	.close-btn {
		position: absolute;
		top: calc(var(--grid-padding) * 2 + #{rs(30)});
		left: calc(var(--grid-padding) * 2 + #{rs(30)});

		@include max-screen($xsmall) {
			top: calc(var(--grid-padding) * 2);
			left: calc(var(--grid-padding) * 2);
		}

		z-index: 2;

		opacity: 0;
	}

	.slider-container {
		position: relative;

		width: 100%;
		border-radius: rs(12);
		overflow: hidden;

		transition: transform 0.5s ease(out-expo), opacity 0.5s ease(out-expo);

		@include max-screen($xsmall) {
			border-radius: 0;
		}

		.photos-slideshow {
			position: relative;

			:deep(.photos-bullets-container) {
				@include max-screen($small) {
					height: 70%;
				}
			}

			:deep(.photos-container) {
				.inner {
					@include max-screen($small) {
						background-color: transparent;
					}
				}

				&:after {
					@include max-screen($small) {
						background-color: transparent;
					}
				}

				.photo-wrapper {
					.photo {
						@include max-screen($small) {
							border-radius: rs(12);
							height: 70%;
						}

						@include max-screen($xsmall) {
							// height: 50%;
							border-radius: 0;
						}
					}
				}

				.content-container {
					@include max-screen($small) {
						top: 74%;
						bottom: initial;
						left: var(--grid-gutter);
						width: n_grid_columns(10);
					}

					@include max-screen($xsmall) {
						width: n_grid_columns(12);
						left: calc(var(--grid-gutter) * 2 + #{n_grid_columns(1)});
					}
				}
			}
		}
	}
}
</style>
